import React, { useMemo } from "react";
import { BPMTable, NumberFormatter } from "../Components";
import "./ModalCumulativeAndDailyTable.scss";
import * as R from "ramda";
import * as Dfns from "date-fns/fp";

interface ModalCumulativeAndDailyTableProps {
  data: any[];
  showCumulative: boolean;
  budget: number;
  goalName: string;
}

const ModalCumulativeAndDailyTable: React.FC<ModalCumulativeAndDailyTableProps> = ({
  data,
  budget,
  goalName,
}) => {
  const dataAdjusted = useMemo(() => {
    const todayFormatted = new Date().toISOString().split("T")[0];
    let remainingBudget = budget;
    const sortedData = data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return sortedData.map(item => {
      const formattedCurrent = new Date(item.date).toISOString().split("T")[0];
      const spend = formattedCurrent < todayFormatted ? item.simpleCleared : item.adjustedProjected;

      let date = R.pipe(Dfns.format("M/dd/yy"))(new Date(item.date));

      remainingBudget -= spend;

      return {
        ...item,
        date,
        remainingBudget,
      };
    });
  }, [data, budget]);

  const headers = useMemo(
    () => [
      {
        label: "Date",
        name: "date",
        width: 100,
        renderer: row => {
          return <div className="budgetDate">{row.date}</div>;
        },
      },
      {
        label: `${goalName === "Budget" ? "Daily Delivery" : `Daily ${goalName}`}`,
        name: "simpleCleared",
        flex: 1,
        minFlexWidth: 150,
        renderer: row => {
          return <NumberFormatter value={row.simpleCleared} type={"$"} decimals={0} />;
        },
      },
      {
        label: `${goalName === "Budget" ? "Expected Delivery" : `Expected ${goalName}`}`,
        name: "simpleBooked",
        flex: 1,
        minFlexWidth: 150,
        renderer: row => {
          return <NumberFormatter value={row.simpleBooked} type={"$"} decimals={0} />;
        },
      },
      {
        label: `${goalName === "Budget" ? "Ideal Delivery" : `Ideal ${goalName}`}`,
        name: "adjustedProjected",
        flex: 1,
        minFlexWidth: 150,
        renderer: row => {
          return <NumberFormatter value={row.adjustedProjected} type={"$"} decimals={0} />;
        },
      },
      {
        label: `${goalName === "Budget" ? "Remaining Budget" : "Difference"}`,
        name: "remainingBudget",
        flex: 1,
        minFlexWidth: 150,
        renderer: row => {
          return <NumberFormatter value={row.remainingBudget} type={"$"} decimals={0} />;
        },
      },
    ],
    [goalName]
  );

  const totals = useMemo(
    () =>
      headers.reduce(
        (acc, { name }) => ({
          ...acc,
          [name]: name === "date" ? "date" : R.sum(dataAdjusted.map(data => data[name])),
        }),
        {}
      ),
    [dataAdjusted, headers]
  );

  const totalsRenderer = ({ data, style, classes }): JSX.Element => (
    <div className={[...classes, "totalContainer"].join(" ")} style={style}>
      {data === "date" ? (
        <span>
          TOTAL / AVG
          <br />
          for entire time period
        </span>
      ) : (
        <span>
          Total
          <br />
          <NumberFormatter value={data} type={"$"} decimals={0} />
        </span>
      )}
    </div>
  );

  return (
    <BPMTable
      className="pacingCumulativeAndDailyTable"
      data={dataAdjusted}
      headers={headers}
      filterBar={false}
      alternateColors={false}
      totals={totals}
      totalsRenderer={totalsRenderer}
    />
  );
};

export default ModalCumulativeAndDailyTable;
